import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { OrderView } from '@guanwei/shared';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { fetchMyOrders } from '@/lib/business.api';
import { quotaApi, type QuotaStatus } from '@/lib/stage3.api';
import { useAuthStore } from '@/lib/auth.store';

const TIER_LABELS: Record<string, string> = {
  free: '游客',
  registered: '注册用户',
  micro: '微会员',
  guanwei: '观微会员',
  vip: 'VIP 会员',
};

const ROLE_LABELS: Record<string, string> = {
  user: '普通用户',
  admin: '管理员',
  superadmin: '超级管理员',
};

const BENEFITS = [
  { key: 'report.full', label: '完整命盘报告' },
  { key: 'report.compatibility', label: '关系合盘报告' },
  { key: 'report.fortune.year', label: '流年报告' },
  { key: 'report.dayun.10year', label: '大运报告' },
  { key: 'chat.daily', label: '每日追问' },
  { key: 'divination.monthly', label: '起卦问事' },
] as const;

const ORDER_STATUS_LABELS: Record<string, string> = {
  pending: '待支付',
  paid: '已支付',
  cancelled: '已取消',
  refunding: '退款中',
  refunded: '已退款',
  failed: '失败',
};

function formatQuota(quota?: QuotaStatus) {
  if (!quota || quota.reason === 'NO_BENEFIT') return '未开通';
  if (quota.unlimited) return '不限次';
  return `剩余 ${quota.remaining} 次`;
}

function formatResetCycle(cycle: string | null | undefined) {
  if (!cycle || cycle === 'never') return '不重置';
  const map: Record<string, string> = {
    daily: '每日重置',
    weekly: '每周重置',
    monthly: '每月重置',
    yearly: '每年重置',
  };
  return map[cycle] ?? cycle;
}

function formatQuotaNote(quota?: QuotaStatus) {
  if (!quota || quota.reason === 'NO_BENEFIT') return '开通会员后可用';
  if (quota.unlimited) return '超级管理员权益 · 不扣次数';
  return `已用 ${quota.used} · ${formatResetCycle(quota.resetCycle)}`;
}

export function ProfilePage() {
  const user = useAuthStore((s) => s.user);
  const [quotas, setQuotas] = useState<Record<string, QuotaStatus>>({});
  const [orders, setOrders] = useState<OrderView[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const benefitKeys = useMemo(() => BENEFITS.map((item) => item.key), []);
  const isSuperadmin = user?.role === 'superadmin';

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }
    setLoading(true);
    Promise.all([quotaApi.query([...benefitKeys]), fetchMyOrders(1, 20)])
      .then(([quotaResult, orderResult]) => {
        setQuotas(quotaResult);
        setOrders(orderResult.list);
      })
      .catch((err) => setError((err as Error).message))
      .finally(() => setLoading(false));
  }, [benefitKeys, user]);

  if (!user) {
    return (
      <main className="flex flex-1 items-center justify-center px-4 sm:px-6">
        <Card className="max-w-md p-6 text-center sm:p-10">
          <p className="mb-3 font-serif text-2xl text-fg">个人中心</p>
          <p className="mb-6 text-sm text-fg-2">登录后查看会员等级、权益余额和订单记录。</p>
          <Link to="/auth/login">
            <Button size="lg">登录</Button>
          </Link>
        </Card>
      </main>
    );
  }

  return (
    <main className="flex-1 px-4 py-8 sm:px-6 sm:py-10">
      <div className="mx-auto max-w-6xl space-y-8">
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.28em] text-accent">Profile</p>
            <h1 className="mt-3 font-serif text-2xl text-fg sm:text-3xl">个人中心</h1>
            <p className="mt-2 text-sm text-fg-muted">{user.email}</p>
          </div>
          <Link to="/membership">
            <Button>{isSuperadmin ? '查看会员套餐' : '升级 / 续费会员'}</Button>
          </Link>
        </div>

        {error && <Card className="p-4 text-sm text-danger">{error}</Card>}

        <section className="grid gap-5 lg:grid-cols-[360px_minmax(0,1fr)]">
          <Card variant="gold" className="p-6">
            <p className="text-sm text-fg-muted">当前会员</p>
            <p className="mt-3 font-serif text-3xl text-accent">
              {isSuperadmin
                ? '超级管理员'
                : (TIER_LABELS[user.membershipTier] ?? user.membershipTier)}
            </p>
            <p className="mt-3 text-sm leading-7 text-fg-2">
              {isSuperadmin
                ? '拥有全部功能权限，权益不限次数'
                : user.membershipExpireAt
                  ? `有效期至 ${new Date(user.membershipExpireAt).toLocaleDateString()}`
                  : user.membershipTier === 'registered' || user.membershipTier === 'free'
                    ? '当前未开通付费会员'
                    : '长期有效'}
            </p>
            <div className="mt-6 rounded-xl border border-border bg-bg-1 p-4 text-sm text-fg-2">
              <p>昵称：{user.name ?? '未设置'}</p>
              <p className="mt-2">角色：{ROLE_LABELS[user.role] ?? user.role}</p>
              <p className="mt-2">注册时间：{new Date(user.createdAt).toLocaleDateString()}</p>
            </div>
          </Card>

          <Card className="p-6">
            <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h2 className="font-serif text-xl text-fg">权益余额</h2>
                <p className="mt-1 text-xs text-fg-muted">展示当前周期内各项权益的剩余次数。</p>
              </div>
              {loading && <span className="text-xs text-fg-muted">读取中…</span>}
            </div>
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
              {BENEFITS.map((benefit) => {
                const quota = quotas[benefit.key];
                return (
                  <div key={benefit.key} className="rounded-xl border border-border bg-bg-2 p-4">
                    <p className="text-sm text-fg">{benefit.label}</p>
                    <p className="mt-2 font-serif text-2xl text-accent">{formatQuota(quota)}</p>
                    <p className="mt-1 text-xs text-fg-muted">{formatQuotaNote(quota)}</p>
                  </div>
                );
              })}
            </div>
          </Card>
        </section>

        <Card className="p-6">
          <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="font-serif text-xl text-fg">购买记录</h2>
              <p className="mt-1 text-xs text-fg-muted">包含会员套餐和单次报告/起卦订单。</p>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-[620px] text-sm">
              <thead className="text-left text-xs text-fg-muted">
                <tr className="border-b border-border">
                  <th className="pb-3">时间</th>
                  <th className="pb-3">商品</th>
                  <th className="pb-3">状态</th>
                  <th className="pb-3 text-right">金额</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {orders.map((order) => (
                  <tr key={order.id}>
                    <td className="py-3 text-xs text-fg-muted">
                      {new Date(order.createdAt).toLocaleString()}
                    </td>
                    <td className="py-3">
                      <p className="text-fg">{order.productName}</p>
                      <p className="mt-1 text-xs text-fg-muted">{order.orderNo}</p>
                    </td>
                    <td className="py-3 text-xs">
                      {ORDER_STATUS_LABELS[order.status] ?? order.status}
                    </td>
                    <td className="py-3 text-right font-mono text-fg">¥{order.payAmountYuan}</td>
                  </tr>
                ))}
                {!loading && orders.length === 0 && (
                  <tr>
                    <td colSpan={4} className="py-10 text-center text-fg-muted">
                      暂无购买记录
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </main>
  );
}
